/**
 * DeliveryTracking - Full-screen live delivery tracking map
 *
 * Shows all In Transit (and recently Delivered) shipments on a Google Map.
 * Each shipment has:
 *  - A departure marker (green)
 *  - An arrival marker (red, if delivered)
 *  - The encoded polyline route
 *
 * Clicking a marker opens a sidebar with shipment + ETA details.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import api from '../../../lib/api';
import {
  Truck, CheckCircle2, MapPin, Clock, Navigation,
  Package, Loader, RefreshCw, X, ChevronRight, Map, AlertTriangle
} from 'lucide-react';

// ── Google Maps loader (singleton) ────────────────────────────────────────────
let googleMapsLoaderPromise = null;
function loadGoogleMaps(apiKey) {
  if (window.google && window.google.maps) return Promise.resolve();
  if (googleMapsLoaderPromise) return googleMapsLoaderPromise;
  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const keyParam = apiKey ? `&key=${apiKey}` : '';
    script.src = `https://maps.googleapis.com/maps/api/js?libraries=marker,places,geometry${keyParam}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (e) => reject(e);
    document.head.appendChild(script);
  });
  return googleMapsLoaderPromise;
}

function formatDuration(seconds) {
  if (!seconds) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m} min`;
}

function formatDistance(meters) {
  if (!meters) return null;
  return `${(meters / 1609.34).toFixed(1)} mi`;
}

// Calculate distance between two lat/lng points using Haversine formula
function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000; // Earth radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in meters
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function DeliveryTracking() {
  const { isDark } = useTheme();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);   // flat list of all google.maps.Marker instances
  const polylinesRef = useRef([]); // flat list of all google.maps.Polyline instances
  const infoWindowRef = useRef(null);

  const [mapReady, setMapReady] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Load Google Maps
  useEffect(() => {
    loadGoogleMaps(apiKey).then(() => setMapReady(true)).catch(console.error);
  }, [apiKey]);

  // Init map
  useEffect(() => {
    if (!mapReady || !mapRef.current || mapInstanceRef.current) return;
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: 39.5, lng: -98.35 },
      zoom: 5,
      mapTypeId: 'roadmap',
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      styles: isDark ? DARK_MAP_STYLES : [],
    });
    infoWindowRef.current = new window.google.maps.InfoWindow();
  }, [mapReady, isDark]);

  // Fetch tracking data
  const fetchRoutes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/delivery/tracking');
      console.log('[DeliveryTracking] Fetched routes:', {
        count: data?.length || 0,
        routesWithPolyline: data?.filter(r => r.route_polyline).length || 0,
        routes: data?.map(r => ({
          id: r.id,
          shipmentId: r.shipment_id,
          hasPolyline: !!r.route_polyline,
          polylineLength: r.route_polyline?.length || 0,
          status: r.status
        })) || []
      });
      // Sort routes: delivered first, then in_transit
      const sorted = (data || []).sort((a, b) => {
        if (a.status === 'delivered' && b.status !== 'delivered') return -1;
        if (a.status !== 'delivered' && b.status === 'delivered') return 1;
        return 0;
      });
      setRoutes(sorted);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch delivery tracking:', err);
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoutes();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchRoutes, 60000);
    return () => clearInterval(interval);
  }, [fetchRoutes]);

  // Draw all routes on the map whenever data or map changes
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || routes.length === 0) return;

    const map = mapInstanceRef.current;

    // Clear old markers and polylines
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    polylinesRef.current.forEach(p => p.setMap(null));
    polylinesRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();

    // Color palette for different routes
    const routeColors = [
      '#7c3aed', // purple
      '#3b82f6', // blue
      '#10b981', // green
      '#f59e0b', // amber
      '#ef4444', // red
      '#8b5cf6', // violet
      '#06b6d4', // cyan
      '#ec4899', // pink
    ];

    routes.forEach((route, index) => {
      const isDelivered = route.status === 'delivered';
      const isSelected = selectedRoute && selectedRoute.id === route.id;
      
      // Check if delivery is outside geofence (wrong location)
      const geofenceValue = route.geofence_inside;
      const isWrongDelivery = isDelivered && (geofenceValue === false || geofenceValue === 'false' || geofenceValue === 0);
      
      // If a route is selected, only show that route. Otherwise show all.
      if (selectedRoute && !isSelected) {
        return; // Skip this route - don't draw it
      }
      
      // Get color for this route (cycle through palette)
      // Use red color for wrong deliveries
      const routeColor = isWrongDelivery ? '#ef4444' : routeColors[index % routeColors.length];

      // ── Departure marker ──────────────────────────────────────────────────
      if (route.origin_lat && route.origin_lng) {
        const originPos = {
          lat: parseFloat(route.origin_lat),
          lng: parseFloat(route.origin_lng),
        };
        const depMarker = new window.google.maps.Marker({
          position: originPos,
          map,
          title: `${route.delivery_no || route.shipment_no || 'Shipment'} — Departure`,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: isSelected ? 11 : 9,
            fillColor: '#22c55e',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: isSelected ? 3 : 2,
          },
          zIndex: isSelected ? 10 : 5,
        });
        depMarker.addListener('click', () => setSelectedRoute(route));
        markersRef.current.push(depMarker);
        bounds.extend(originPos);
      }

      // ── Arrival / destination marker ──────────────────────────────────────
      if (route.destination_lat && route.destination_lng) {
        const destPos = {
          lat: parseFloat(route.destination_lat),
          lng: parseFloat(route.destination_lng),
        };
        // Use different marker style for wrong deliveries
        const markerColor = isWrongDelivery ? '#dc2626' : (isDelivered ? '#ef4444' : '#f59e0b');
        const markerScale = isWrongDelivery ? (isSelected ? 13 : 11) : (isSelected ? 11 : 9);
        const markerStroke = isWrongDelivery ? 4 : (isSelected ? 3 : 2);
        
        const arrMarker = new window.google.maps.Marker({
          position: destPos,
          map,
          title: `${route.delivery_no || route.shipment_no || 'Shipment'} — ${isWrongDelivery ? 'Wrong Location!' : (isDelivered ? 'Delivered' : 'Destination')}`,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: markerScale,
            fillColor: markerColor,
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: markerStroke,
          },
          zIndex: isWrongDelivery ? 15 : (isSelected ? 10 : 5),
        });
        arrMarker.addListener('click', () => setSelectedRoute(route));
        markersRef.current.push(arrMarker);
        bounds.extend(destPos);
      }

      // ── Wrong delivery: show intended location marker ──────────────────────
      if (isWrongDelivery && route.delivery_address_lat && route.delivery_address_lng) {
        const intendedPos = {
          lat: parseFloat(route.delivery_address_lat),
          lng: parseFloat(route.delivery_address_lng),
        };
        
        // Green marker for intended location
        const intendedMarker = new window.google.maps.Marker({
          position: intendedPos,
          map,
          title: `${route.delivery_no || route.shipment_no || 'Shipment'} — Intended Delivery Location`,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: isSelected ? 11 : 9,
            fillColor: '#22c55e',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: isSelected ? 3 : 2,
          },
          label: {
            text: '✓',
            color: '#ffffff',
            fontSize: '11px',
            fontWeight: 'bold',
          },
          zIndex: isSelected ? 12 : 8,
        });
        intendedMarker.addListener('click', () => setSelectedRoute(route));
        markersRef.current.push(intendedMarker);
        bounds.extend(intendedPos);

        // Draw a line between intended and actual locations
        if (route.destination_lat && route.destination_lng) {
          const actualPos = {
            lat: parseFloat(route.destination_lat),
            lng: parseFloat(route.destination_lng),
          };
          const wrongLocationLine = new window.google.maps.Polyline({
            path: [intendedPos, actualPos],
            geodesic: true,
            strokeColor: '#f97316', // Orange
            strokeOpacity: 0.85,
            strokeWeight: 3,
            zIndex: 11,
            map,
          });
          wrongLocationLine.addListener('click', () => setSelectedRoute(route));
          polylinesRef.current.push(wrongLocationLine);
        }
      }

      // ── Polyline ──────────────────────────────────────────────────────────
      if (route.route_polyline) {
        try {
          const decodedPath = window.google.maps.geometry.encoding.decodePath(route.route_polyline);
          const polyline = new window.google.maps.Polyline({
            path: decodedPath,
            geodesic: true,
            // Use route-specific color, with different opacity for delivered vs in-transit
            // Highlight wrong deliveries with red and thicker line
            strokeColor: routeColor,
            strokeOpacity: isWrongDelivery ? 0.9 : (isDelivered ? 0.5 : 0.8),
            strokeWeight: isWrongDelivery ? (isSelected ? 6 : 5) : (isSelected ? 5 : (isDelivered ? 3 : 4)),
            zIndex: isWrongDelivery ? 12 : (isSelected ? 10 : 1),
            map,
          });
          polyline.addListener('click', () => setSelectedRoute(route));
          polylinesRef.current.push(polyline);
          decodedPath.forEach(pt => bounds.extend(pt));
        } catch (e) {
          console.error('[DeliveryTracking] Could not decode polyline for route', route.id, e, {
            polyline: route.route_polyline?.substring(0, 50) + '...'
          });
        }
      } else {
        console.warn('[DeliveryTracking] No polyline for route:', {
          routeId: route.id,
          shipmentId: route.shipment_id,
          hasOrigin: !!(route.origin_lat && route.origin_lng),
          hasDestination: !!(route.destination_lat && route.destination_lng),
          status: route.status
        });
      }
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { top: 60, right: selectedRoute ? 420 : 60, bottom: 60, left: 60 });
    }
  }, [mapReady, routes, selectedRoute]);

  // ── Sidebar detail panel ──────────────────────────────────────────────────
  const SidebarPanel = () => {
    if (!selectedRoute) return null;
    const r = selectedRoute;
    const isDelivered = r.status === 'delivered';
    // Check if delivery is outside geofence (wrong location)
    const geofenceValue = r.geofence_inside;
    const isWrongDelivery = isDelivered && (geofenceValue === false || geofenceValue === 'false' || geofenceValue === 0);
    const eta = r.estimated_arrival_at ? new Date(r.estimated_arrival_at) : null;
    const etaIsPast = eta && eta < new Date();

    return (
      <div className={`absolute top-0 right-0 h-full w-96 flex flex-col shadow-2xl border-l z-20 overflow-hidden ${
        isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className={`flex items-start justify-between px-5 py-4 border-b flex-shrink-0 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <div>
            <div className="flex items-center gap-2">
              {isDelivered
                ? <CheckCircle2 size={16} className="text-green-400" />
                : <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-pulse" />
              }
              <h3 className={`font-bold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {r.delivery_no || r.shipment_no || 'Shipment'}
              </h3>
            </div>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {isDelivered ? 'Delivered' : 'In Transit'}
            </p>
          </div>
          <button
            onClick={() => setSelectedRoute(null)}
            className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {/* Wrong delivery warning */}
          {isWrongDelivery && (() => {
            let distanceMiles = null;
            if (r.delivery_address_lat && r.delivery_address_lng && r.destination_lat && r.destination_lng) {
              const intendedLat = parseFloat(r.delivery_address_lat);
              const intendedLng = parseFloat(r.delivery_address_lng);
              const actualLat = parseFloat(r.destination_lat);
              const actualLng = parseFloat(r.destination_lng);
              const distanceMeters = calculateDistance(intendedLat, intendedLng, actualLat, actualLng);
              distanceMiles = distanceMeters / 1609.34;
            }
            return (
              <div className={`rounded-xl border p-4 ${isDark ? 'border-red-700 bg-red-900/20' : 'border-red-200 bg-red-50'}`}>
                <div className={`text-xs font-semibold mb-1.5 flex items-center gap-2 ${isDark ? 'text-red-300' : 'text-red-700'}`}>
                  <AlertTriangle size={14} />
                  Delivered to Wrong Location
                </div>
                <div className={`text-xs ${isDark ? 'text-red-200' : 'text-red-600'}`}>
                  {distanceMiles !== null
                    ? `This shipment was delivered ${distanceMiles.toFixed(2)} miles away from the intended delivery address.`
                    : 'This shipment was delivered outside the intended delivery geofence.'}
                </div>
              </div>
            );
          })()}

          {/* ETA / Arrival card */}
          <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
            <div className={`text-xs font-medium uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {isDelivered ? 'Delivery Summary' : 'ETA & Route'}
            </div>
            <div className="space-y-2.5">
              {eta && !isDelivered && (
                <div className="flex items-center gap-3">
                  <Clock size={15} className={etaIsPast ? 'text-red-400' : 'text-amber-400'} />
                  <div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Estimated Arrival</div>
                    <div className={`text-sm font-semibold ${etaIsPast ? 'text-red-400' : isDark ? 'text-amber-300' : 'text-amber-600'}`}>
                      {etaIsPast ? 'Overdue — ' : ''}{eta.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      <span className={`text-xs font-normal ml-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {eta.toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {r.actual_arrival_at && (
                <div className="flex items-center gap-3">
                  <CheckCircle2 size={15} className="text-green-400" />
                  <div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Actual Arrival</div>
                    <div className={`text-sm font-semibold ${isDark ? 'text-green-300' : 'text-green-600'}`}>
                      {new Date(r.actual_arrival_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      <span className={`text-xs font-normal ml-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {new Date(r.actual_arrival_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {r.estimated_distance_meters && (
                <div className="flex items-center gap-3">
                  <Navigation size={15} className={isDark ? 'text-violet-400' : 'text-violet-600'} />
                  <div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Route Distance</div>
                    <div className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      {formatDistance(r.estimated_distance_meters)}
                      {r.estimated_duration_seconds && (
                        <span className={`text-xs font-normal ml-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          · {formatDuration(r.estimated_duration_seconds)} drive
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Shipment info */}
          <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
            <div className={`text-xs font-medium uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Shipment Info
            </div>
            <div className="space-y-2">
              {[
                ['Customer', r.customer_name],
                ['Destination', r.customer_address],
                ['Carrier', r.carrier_name],
                ['Stop #', r.stop_no != null ? `#${r.stop_no}` : null],
                ['Quantity', r.box_quantity],
                ['Material', r.material_desc],
              ].filter(([, v]) => v != null && v !== '').map(([label, value]) => (
                <div key={label} className="flex items-start justify-between gap-3">
                  <span className={`text-xs flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{label}</span>
                  <span className={`text-xs text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{String(value)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Location trail */}
          {(r.origin_lat || r.destination_lat) && (
            <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
              <div className={`text-xs font-medium uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Location Trail
              </div>
              <div className="relative pl-4">
                {/* Vertical line */}
                <div className={`absolute left-1.5 top-2 bottom-2 w-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />

                {r.origin_lat && (
                  <div className="relative mb-4">
                    <div className="absolute -left-2.5 top-1 w-3 h-3 rounded-full bg-green-500 border-2 border-white dark:border-gray-900" />
                    <div className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Departed</div>
                    <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {r.origin_address || `${parseFloat(r.origin_lat).toFixed(5)}, ${parseFloat(r.origin_lng).toFixed(5)}`}
                    </div>
                  </div>
                )}

                {r.destination_lat ? (
                  <div className="relative">
                    <div className={`absolute -left-2.5 top-1 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900 ${isDelivered ? 'bg-red-500' : 'bg-amber-400'}`} />
                    <div className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {isDelivered ? 'Delivered' : 'Destination'}
                    </div>
                    <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {r.destination_address || r.customer_address || `${parseFloat(r.destination_lat).toFixed(5)}, ${parseFloat(r.destination_lng).toFixed(5)}`}
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <div className={`absolute -left-2.5 top-1 w-3 h-3 rounded-full border-2 border-dashed ${isDark ? 'border-gray-600' : 'border-gray-300'} bg-transparent`} />
                    <div className={`text-xs font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Destination</div>
                    <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                      {r.customer_address || 'Awaiting delivery'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Shipment list sidebar (left) ──────────────────────────────────────────
  const inTransitRoutes = routes.filter(r => r.status === 'in_transit');
  const deliveredRoutes = routes.filter(r => r.status === 'delivered');

  return (
    <div className="relative h-full w-full overflow-hidden">

      {/* Map canvas */}
      <div ref={mapRef} className="w-full h-full" />

      {/* Loading overlay */}
      {(!mapReady || loading) && (
        <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
          <div className="flex flex-col items-center gap-3">
            <Loader size={28} className="animate-spin text-violet-500" />
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading delivery tracking…</p>
          </div>
        </div>
      )}

      {/* Empty state overlay */}
      {mapReady && !loading && routes.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className={`rounded-2xl border px-8 py-6 text-center shadow-xl ${isDark ? 'bg-gray-900/90 border-gray-700' : 'bg-white/95 border-gray-200'}`}>
            <Map size={36} className={`mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>No active deliveries</p>
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Shipments will appear here once moved to In Transit
            </p>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
        <div className={`flex items-center gap-3 px-4 py-2.5 rounded-xl shadow-xl border backdrop-blur-sm ${isDark ? 'bg-gray-900/80 border-gray-700' : 'bg-white/95 border-gray-200'}`}>
          <Truck size={16} className="text-violet-400" />
          <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Live Delivery Tracking
          </span>
          {inTransitRoutes.length > 0 && (
            <span className="flex items-center gap-1.5 text-xs font-medium text-amber-400">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              {inTransitRoutes.length} in transit
            </span>
          )}
          {deliveredRoutes.length > 0 && (
            <span className="flex items-center gap-1.5 text-xs font-medium text-green-400">
              <CheckCircle2 size={12} />
              {deliveredRoutes.length} delivered
            </span>
          )}
        </div>

        <button
          onClick={fetchRoutes}
          disabled={loading}
          className={`p-2.5 rounded-xl shadow-xl border backdrop-blur-sm transition-colors ${
            isDark
              ? 'bg-gray-900/80 border-gray-700 text-gray-400 hover:text-gray-200'
              : 'bg-white/95 border-gray-200 text-gray-500 hover:text-gray-700'
          }`}
          title="Refresh"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>

        {lastRefresh && (
          <div className={`px-3 py-2 rounded-xl text-xs shadow-xl border backdrop-blur-sm ${isDark ? 'bg-gray-900/80 border-gray-700 text-gray-500' : 'bg-white/95 border-gray-200 text-gray-400'}`}>
            Updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>

      {/* Shipment list (bottom-left) */}
      {routes.length > 0 && (
        <div className={`absolute bottom-4 left-3 z-10 w-72 max-h-64 rounded-2xl shadow-2xl border overflow-hidden ${isDark ? 'bg-gray-900/90 border-gray-700' : 'bg-white/95 border-gray-200'}`}>
          <div className={`px-4 py-2.5 border-b text-xs font-semibold uppercase tracking-wider ${isDark ? 'border-gray-800 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
            Shipments
          </div>
          <div className="overflow-y-auto max-h-48 divide-y divide-gray-800/50">
            {routes.map(r => {
              const isDelivered = r.status === 'delivered';
              const isSelected = selectedRoute?.id === r.id;
              // Check if delivery is outside geofence (wrong location)
              const geofenceValue = r.geofence_inside;
              const isWrongDelivery = isDelivered && (geofenceValue === false || geofenceValue === 'false' || geofenceValue === 0);
              return (
                <button
                  key={r.id}
                  onClick={() => setSelectedRoute(isSelected ? null : r)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    isWrongDelivery
                      ? isSelected
                        ? isDark ? 'bg-red-900/40 border-l-2 border-red-500' : 'bg-red-50 border-l-2 border-red-500'
                        : isDark ? 'bg-red-900/20 hover:bg-red-900/30' : 'bg-red-50/50 hover:bg-red-50'
                      : isSelected
                        ? isDark ? 'bg-violet-900/30' : 'bg-violet-50'
                        : isDark ? 'hover:bg-gray-800/60' : 'hover:bg-gray-50'
                  }`}
                >
                  {isWrongDelivery
                    ? <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
                    : isDelivered
                      ? <CheckCircle2 size={14} className="text-green-400 flex-shrink-0" />
                      : <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
                  }
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      {r.delivery_no || r.shipment_no || r.shipment_id?.slice(0, 8)}
                    </div>
                    <div className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {isWrongDelivery ? 'Wrong Location' : (r.customer_name || r.customer_address || '—')}
                    </div>
                  </div>
                  <ChevronRight size={12} className={isDark ? 'text-gray-600' : 'text-gray-300'} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Right detail panel */}
      <SidebarPanel />
    </div>
  );
}

// ── Dark map styles ───────────────────────────────────────────────────────────
const DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#1d2535' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1d2535' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8a9bb0' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2d3748' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1a202c' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3d4f6b' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
];
